/**
 * get-started — step controller
 */
(() => {
  const STORAGE_KEY = 'state.v1';
  const $root = document.getElementById('get-started-root');
  if (!$root) return;

  function readBoot() {
    // Prefer explicit window boot if present, else read the inline JSON script.
    const w = window.GetStartedBoot;
    if (w && typeof w === 'object') return w;
    const el = document.getElementById('get-started-boot');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  const Storage = window.GetStartedStorage;
  const Boot = readBoot();
  if (!Storage || !Boot || !Boot.flowUrl) {
    console.error('[get-started] Missing GetStartedStorage or GetStartedBoot.flowUrl');
    return;
  }

  /** UI steps: device=1, goals=2; computing + results share step 3; precheckout=4 (count step hidden). */
  const UI_STEP_MAX = 4;
  const PANEL_TRANSITION_MS = 400;
  /** Results step: show the same floating Continue as device once the user has scrolled this far (px). */
  const RESULTS_FLOAT_SCROLL_THRESHOLD_PX = 320;

  const $panels = $root.querySelectorAll('[data-get-started-panel]');
  const $prev = document.getElementById('get-started-prev');
  const $next = document.getElementById('get-started-next');
  const $stepbar = document.getElementById('get-started-stepbar');
  const $footer =
    document.getElementById('get-started-main-footer') ||
    $root.querySelector('.get-started-section__footer:not(#get-started-goals-float-footer)');
  const $footerContext = document.getElementById('get-started-footer-context');
  const $footerContextTitle = document.getElementById('get-started-footer-context-title');
  const $footerContextSubtitle = document.getElementById('get-started-footer-context-subtitle');
  const $footerContextCheck = document.getElementById('get-started-footer-context-check');
  const $footerContextSubtitleText = document.getElementById(
    'get-started-footer-context-subtitle-text'
  );
  const $floatingNext = document.getElementById('get-started-floating-next');
  const $floatingWrap = document.getElementById('get-started-floating-wrap');
  const $goalsFloatFooter = document.getElementById('get-started-goals-floating-footer');
  const $goalsFloatFooterRow = document.getElementById('get-started-goals-float-footer');
  const $goalsFloatNext = document.getElementById('get-started-goals-float-next');
  const $floatFooterContext = document.getElementById(
    'get-started-goals-float-footer-context'
  );
  const $floatFooterContextTitle = document.getElementById(
    'get-started-goals-float-footer-context-title'
  );
  const $floatFooterContextSubtitle = document.getElementById(
    'get-started-goals-float-footer-context-subtitle'
  );
  const $floatFooterContextCheck = document.getElementById(
    'get-started-goals-float-footer-context-check'
  );
  const $floatFooterContextSubtitleText = document.getElementById(
    'get-started-goals-float-footer-context-subtitle-text'
  );
  const $sectionInner = $root.querySelector('.get-started-section__inner');

  /** Avoid aria-hidden on an ancestor of the focused element (browser a11y violation). */
  function blurActiveElementIfInside(container) {
    if (!container || typeof container.contains !== 'function') return;
    const ae = document.activeElement;
    if (ae && ae !== document.body && container.contains(ae)) {
      try {
        ae.blur();
      } catch (_) {
        /* ignore */
      }
    }
  }

  /**
   * DOM `id`s hidden during the quiz (`display: none`) and shown as `display: block` on precheckout.
   * Use the wrapper id from the storefront (often `shopify-section-<long id>`).
   */
  const PRECHECKOUT_REVEAL_SECTION_IDS = [
    'nc-case-study-section',
    'ppfaq-section',
    'ppmq-section',
    'judgeme_product_reviews'
  ];

  let activePanelId = null;
  let panelTransitionToken = 0;
  let floatingVisible = false;
  let floatingHideTimer = null;
  let goalsFloatVisible = false;
  let goalsFloatHideTimer = null;

  const State = {
    flow: null,
    stepId: null,
    answers: {
      devices: [],
      goals: [],
      supplementCount: null,
    },
    computed: null,
    history: [],
  };

  function defaultState() {
    return {
      v: 1,
      stepId: 'device',
      /* supplementCount was null until the count step; that step is skipped — default 4. */
      answers: { devices: [], goals: [], supplementCount: 4 },
      computed: null,
      history: ['device'],
    };
  }

  function makeGoalKey(deviceId, goalId) {
    return `${deviceId}::${goalId}`;
  }

  function parseGoalKey(key) {
    if (typeof key !== 'string') return { deviceId: null, goalId: '' };
    const sep = '::';
    const i = key.indexOf(sep);
    if (i < 0) return { deviceId: null, goalId: key };
    return { deviceId: key.slice(0, i), goalId: key.slice(i + sep.length) };
  }

  /** Legacy saved goals were plain goal ids; migrate when exactly one device. */
  function migrateLegacyGoals(answers) {
    if (!Array.isArray(answers.goals) || !answers.goals.length) return;
    const devices = answers.devices || [];
    answers.goals = answers.goals
      .map((k) => {
        if (typeof k !== 'string') return null;
        if (k.includes('::')) return k;
        if (devices.length === 1) return makeGoalKey(devices[0], k);
        return null;
      })
      .filter(Boolean);
  }

  function normalizeAnswers(raw) {
    const a = {
      devices: [],
      goals: [],
      supplementCount: raw?.supplementCount ?? null,
    };
    if (raw && Array.isArray(raw.devices) && raw.devices.length) {
      a.devices = [...raw.devices];
    } else if (raw && raw.device) {
      a.devices = [raw.device];
    } else if (raw && Array.isArray(raw.devices)) {
      a.devices = [...raw.devices];
    }
    if (raw && Array.isArray(raw.goals)) {
      a.goals = [...raw.goals];
    }
    migrateLegacyGoals(a);
    return a;
  }

  /** Supplement count panel is skipped; use 4 when the user has not chosen another value. */
  function ensureDefaultSupplementCountForSkip() {
    if (State.answers.supplementCount == null) State.answers.supplementCount = 4;
  }

  /** Remove count from saved history so Back matches the shortened flow. */
  function normalizeHistorySkipCountStep(hist) {
    if (!Array.isArray(hist) || hist.length === 0) return ['device'];
    const filtered = hist.filter((id) => id !== 'count');
    return filtered.length ? filtered : ['device'];
  }

  function pruneGoalsToDevices() {
    const allowed = new Set(State.answers.devices || []);
    State.answers.goals = State.answers.goals.filter((key) => {
      const { deviceId } = parseGoalKey(key);
      return deviceId && allowed.has(deviceId);
    });
  }

  function loadSaved() {
    const saved = Storage.load(STORAGE_KEY);
    if (!saved || typeof saved !== 'object') return;
    State.stepId = saved.stepId || 'device';
    /* Mid “computing” or saved count step: return to goals (count panel no longer shown). */
    if (State.stepId === 'computing' || State.stepId === 'count') {
      State.stepId = 'goals';
    }
    State.answers = normalizeAnswers(saved.answers || {});
    State.computed = saved.computed || null;
    const rawHist =
      Array.isArray(saved.history) && saved.history.length ? saved.history : ['device'];
    State.history = normalizeHistorySkipCountStep(rawHist);
    if (State.stepId === 'results' && State.history.indexOf('results') === -1) {
      State.history = ['device', 'goals', 'results'];
    }
    ensureDefaultSupplementCountForSkip();
  }

  function syncWindowGetStartedState() {
    window.getStartedState = {
      v: 1,
      stepId: State.stepId,
      answers: { ...State.answers },
      computed: State.computed,
      flow: State.flow,
    };
  }

  /** Meta / Google Ads — once per browser tab session per step (sessionStorage). */
  const GET_STARTED_PAGELOAD_EVENTS = {
    device: 'uniqueSelectDevicePageLoad',
    goals: 'uniqueSelectGoalPageLoad',
    results: 'uniqueResultPageLoad',
    precheckout: 'uniquePreCheckoutPageLoad',
  };

  function trackGetStartedStepPageLoadOnce(stepId) {
    const eventName = GET_STARTED_PAGELOAD_EVENTS[stepId];
    if (!eventName) return;
    try {
      const storageKey = `${eventName}Tracked`;
      if (sessionStorage.getItem(storageKey)) return;
      if (typeof trackMetaPixel === 'function') trackMetaPixel(eventName);
      if (typeof trackGoogle === 'function') trackGoogle(eventName);
      sessionStorage.setItem(storageKey, 'true');
    } catch (_) {
      /* sessionStorage or trackers may be unavailable */
    }
  }

  function persist() {
    Storage.save(
      STORAGE_KEY,
      {
        stepId: State.stepId,
        answers: State.answers,
        computed: State.computed,
        history: State.history,
      },
      { ttlMs: Storage.DEFAULT_TTL_MS }
    );
    syncWindowGetStartedState();
  }

  function fetchFlow(url) {
    return fetch(url, { cache: 'no-store' }).then((r) => {
      if (!r.ok) throw new Error(`Flow load failed: ${r.status}`);
      return r.json();
    });
  }

  /** Dedupe `<link rel="preload" as="image">` hints for get-started (device, goals, results pills). */
  const preloadedQuizImageUrls = new Set();

  function preloadQuizImageUrls(urls) {
    if (!Array.isArray(urls) || !urls.length) return;
    const head = document.head;
    if (!head) return;
    urls.forEach((u) => {
      if (!u || typeof u !== 'string' || preloadedQuizImageUrls.has(u)) return;
      preloadedQuizImageUrls.add(u);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = u;
      head.appendChild(link);
    });
  }

  /** Step 1: the three large device card images (not logos). */
  function collectDeviceStepCardImageSrcs() {
    const panel = document.getElementById('get-started-panel-device');
    if (!panel) return [];
    return Array.from(panel.querySelectorAll('.get-started-step-device__card-img[src]'))
      .map((img) => img.getAttribute('src'))
      .filter(Boolean);
  }

  function collectGoalCatalogImageSrcs(flow) {
    const urls = [];
    const cat = flow && flow.goalCatalog;
    if (!cat || typeof cat !== 'object') return urls;
    Object.keys(cat).forEach((deviceId) => {
      const goals = cat[deviceId];
      if (!Array.isArray(goals)) return;
      goals.forEach((g) => {
        if (g && typeof g.image === 'string' && g.image) urls.push(g.image);
      });
    });
    return urls;
  }

  /** `window.pills` — supplement thumbnail URLs used on results / metric popups (theme global). */
  function collectPillsDictionaryImageSrcs() {
    const p = typeof window !== 'undefined' && window.pills;
    if (!p || typeof p !== 'object') return [];
    return Object.values(p).filter((v) => {
      if (typeof v !== 'string' || !v.trim()) return false;
      return (
        v.startsWith('http://') ||
        v.startsWith('https://') ||
        v.startsWith('//') ||
        v.startsWith('/')
      );
    });
  }

  function preloadGetStartedDeviceImages() {
    preloadQuizImageUrls(collectDeviceStepCardImageSrcs());
  }

  function preloadGetStartedGoalsAndPillsImages(flow) {
    preloadQuizImageUrls(collectGoalCatalogImageSrcs(flow));
    const runPills = () => preloadQuizImageUrls(collectPillsDictionaryImageSrcs());
    runPills();
    setTimeout(runPills, 120);
    setTimeout(runPills, 500);
  }

  /** stepIndex: 0 = none (initial), 1–4 = UI steps (computing + results both = 3; precheckout = 4; count removed). */
  function updateStepIndicator(stepIndex) {
    if (!$stepbar) return;
    const clamped = Math.max(0, Math.min(UI_STEP_MAX, stepIndex | 0));
    $stepbar.setAttribute('data-current-step', String(clamped));

    const dots = $stepbar.querySelectorAll('[data-get-started-step-index]');
    dots.forEach((dot) => {
      const n = parseInt(dot.getAttribute('data-get-started-step-index'), 10);
      dot.classList.remove('is-done', 'is-current', 'is-upcoming');
      dot.removeAttribute('aria-label');
      dot.setAttribute('tabindex', '-1');
      if (clamped === 0) {
        dot.classList.add('is-upcoming');
      } else if (n < clamped) {
        dot.classList.add('is-done');
        dot.setAttribute('tabindex', '0');
        dot.setAttribute('aria-label', `Go to step ${n}`);
      } else if (n === clamped) {
        dot.classList.add('is-current');
      } else {
        dot.classList.add('is-upcoming');
      }
    });
  }

  /** History prefix matching the forward flow (computing is not a persisted history segment). */
  function canonicalHistoryForStepId(stepId) {
    if (stepId === 'device') return ['device'];
    if (stepId === 'goals') return ['device', 'goals'];
    if (stepId === 'results' || stepId === 'computing') return ['device', 'goals', 'results'];
    if (stepId === 'precheckout') return ['device', 'goals', 'results', 'precheckout'];
    return ['device'];
  }

  function uiStepIndexToStepId(uiStep) {
    const map = { 1: 'device', 2: 'goals', 3: 'results', 4: 'precheckout' };
    return map[uiStep] || null;
  }

  /** Jump to a completed step via step-bar (only `.is-done` dots call this). */
  function jumpToStepBarIndex(uiStep) {
    const stepId = uiStepIndexToStepId(uiStep);
    if (!stepId || !$stepbar) return;
    const dot = $stepbar.querySelector(`[data-get-started-step-index="${uiStep}"]`);
    if (!dot || !dot.classList.contains('is-done')) return;
    const currentIdx = stepIndexFromId(State.stepId);
    if (uiStep >= currentIdx) return;

    State.stepId = stepId;
    State.history = canonicalHistoryForStepId(stepId);
    setPanel(stepId);
    if (stepId === 'goals') {
      renderGoalsGrid();
    }
    if (stepId === 'results') {
      renderResults();
    }
    if (stepId === 'precheckout') {
      renderPrecheckout();
    }
    persist();
    updateNav();
    trackGetStartedStepPageLoadOnce(stepId);
  }

  function bindStepBarDotClicks() {
    if (!$stepbar) return;
    $stepbar.addEventListener('click', (e) => {
      const dot = e.target.closest('[data-get-started-step-index]');
      if (!dot || !$stepbar.contains(dot) || !dot.classList.contains('is-done')) return;
      e.preventDefault();
      const uiStep = parseInt(dot.getAttribute('data-get-started-step-index'), 10);
      jumpToStepBarIndex(uiStep);
    });
    $stepbar.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const dot = e.target.closest('[data-get-started-step-index]');
      if (!dot || !$stepbar.contains(dot) || !dot.classList.contains('is-done')) return;
      e.preventDefault();
      const uiStep = parseInt(dot.getAttribute('data-get-started-step-index'), 10);
      jumpToStepBarIndex(uiStep);
    });
  }

  function stepIndexFromId(stepId) {
    if (stepId === 'precheckout') return 4;
    if (stepId === 'computing' || stepId === 'results') return 3;
    /* Legacy saves on count step map to the goals segment visually. */
    if (stepId === 'count') return 2;
    const map = { device: 1, goals: 2 };
    return map[stepId] || 0;
  }

  function getPanelEl(id) {
    return $root.querySelector(`[data-get-started-panel="${id}"]`);
  }

  function syncPrecheckoutRevealSections(panelId) {
    if (!PRECHECKOUT_REVEAL_SECTION_IDS.length) return;
    const show = panelId === 'precheckout';
    for (let i = 0; i < PRECHECKOUT_REVEAL_SECTION_IDS.length; i++) {
      const el = document.getElementById(PRECHECKOUT_REVEAL_SECTION_IDS[i]);
      if (el) el.style.display = show ? 'block' : 'none';
    }
  }

  /** Precheckout is full-bleed; other steps use theme `page-width` on the section inner. Root data attr drives step-specific CSS (e.g. back arrow, footer). */
  function syncSectionInnerPageWidth(panelId) {
    if ($root && panelId) {
      $root.setAttribute('data-get-started-active-step', panelId);
    }
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.toggle('get-started--precheckout', panelId === 'precheckout');
    }
    if (!$sectionInner) {
      syncPrecheckoutRevealSections(panelId);
      return;
    }
    if (panelId === 'precheckout') {
      $sectionInner.classList.remove('page-width');
    } else if (!$sectionInner.classList.contains('page-width')) {
      $sectionInner.classList.add('page-width');
    }
    syncPrecheckoutRevealSections(panelId);
    applySectionInnerMinHeightPx();
  }

  /**
   * Lock inner min-height to a px value: 100dvh − --gs-root-padding-top − --gs-inner-min-subtract
   * (avoids dvh/safe-area drift; recomputed on resize).
   */
  function applySectionInnerMinHeightPx() {
    if (!$sectionInner || !$root) return;
    const probeDvh = document.createElement('div');
    probeDvh.style.cssText =
      'position:absolute;left:0;top:0;width:0;height:100dvh;visibility:hidden;pointer-events:none;overflow:hidden;display:block!important;';
    document.documentElement.appendChild(probeDvh);
    const dvhPx = probeDvh.offsetHeight;
    document.documentElement.removeChild(probeDvh);

    const probeRoot = document.createElement('div');
    probeRoot.style.cssText =
      'position:absolute;left:0;top:0;width:0;height:var(--gs-root-padding-top);visibility:hidden;pointer-events:none;overflow:hidden;display:block!important;';
    $root.appendChild(probeRoot);
    const rootPadPx = probeRoot.offsetHeight;
    $root.removeChild(probeRoot);

    const probeSub = document.createElement('div');
    probeSub.style.cssText =
      'position:absolute;left:0;top:0;width:0;height:var(--gs-inner-min-subtract);visibility:hidden;pointer-events:none;overflow:hidden;display:block!important;';
    $sectionInner.appendChild(probeSub);
    const subtractPx = probeSub.offsetHeight;
    $sectionInner.removeChild(probeSub);

    const px = Math.max(0, Math.round(dvhPx - rootPadPx - subtractPx));
    $sectionInner.style.minHeight = `${px}px`;
  }

  function setPanel(id, opts = {}) {
    const { skipIndicator = false } = opts;
    const next = getPanelEl(id);
    if (!next) return;

    if (activePanelId === id) {
      if (!skipIndicator) {
        updateStepIndicator(stepIndexFromId(id));
      }
      syncSectionInnerPageWidth(id);
      return;
    }

    const prev = activePanelId ? getPanelEl(activePanelId) : null;
    const myToken = ++panelTransitionToken;

    function applyStepIndicatorAfterShow() {
      if (!skipIndicator) {
        updateStepIndicator(stepIndexFromId(id));
      }
    }

    function showNext() {
      if (myToken !== panelTransitionToken) return;
      next.style.display = 'block';
      next.setAttribute('aria-hidden', 'false');
      void next.offsetHeight;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (myToken !== panelTransitionToken) return;
          next.classList.add('is-panel-visible');
          activePanelId = id;
          syncSectionInnerPageWidth(id);
          applyStepIndicatorAfterShow();
        });
      });
    }

    function finishHide() {
      if (myToken !== panelTransitionToken) return;
      if (prev) {
        blurActiveElementIfInside(prev);
        prev.style.display = 'none';
        prev.classList.remove('is-panel-visible');
        prev.setAttribute('aria-hidden', 'true');
      }
      // During the gap where both panels are hidden, reset viewport to top.
      window.scrollTo({ top: 0, behavior: 'auto' });
      showNext();
    }

    if (!prev) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      showNext();
      return;
    }

    if (!prev.classList.contains('is-panel-visible')) {
      blurActiveElementIfInside(prev);
      prev.style.display = 'none';
      prev.setAttribute('aria-hidden', 'true');
      window.scrollTo({ top: 0, behavior: 'auto' });
      showNext();
      return;
    }

    prev.classList.remove('is-panel-visible');

    let done = false;
    const doneOnce = () => {
      if (done) return;
      done = true;
      finishHide();
    };

    const t = window.setTimeout(doneOnce, PANEL_TRANSITION_MS + 80);

    const onEnd = (e) => {;
      if (e.propertyName !== 'opacity') return;
      prev.removeEventListener('transitionend', onEnd);
      window.clearTimeout(t);
      doneOnce();
    };

    prev.addEventListener('transitionend', onEnd);
  }

  function goalsForDevice(deviceId) {
    const cat = State.flow?.goalCatalog?.[deviceId];
    return Array.isArray(cat) ? cat : [];
  }

  function deviceLabel(deviceId) {
    return State.flow?.devices?.[deviceId]?.label || deviceId;
  }

  function goalFromCatalog(deviceId, goalId) {
    return goalsForDevice(deviceId).find((g) => g.id === goalId) || null;
  }

  function isSleepGoalId(goalId) {
    if (!goalId || typeof goalId !== 'string') return false;
    return goalId === 'sleep' || goalId === 'sleep_garmin' || goalId.startsWith('sleep_');
  }

  function goalSummaryLabel(goalKey) {
    const { deviceId, goalId } = parseGoalKey(goalKey);
    const goal = goalFromCatalog(deviceId, goalId);
    const goalText =
      (goal && goal.label) ||
      (isSleepGoalId(goalId) ? 'Sleep' : String(goalId || '').replace(/_/g, ' '));
    const deviceText = deviceLabel(deviceId || '').toUpperCase();
    const multipleDevices = (State.answers.devices || []).length > 1;
    if (!goalText) return deviceText;
    if (!multipleDevices || !deviceText) return goalText;
    if (isSleepGoalId(goalId)) return goalText;
    return `${goalText} (${deviceText})`;
  }

  const DEFAULT_GOAL_IMAGE =
    'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=320&fit=crop&q=80';

  function createGoalCard(goalKey, g) {
    const card = document.createElement('div');
    card.className =
      'get-started-goal-card card-external-shadow get-started__goal-card';
    card.dataset.goalKey = goalKey;
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-pressed', 'false');

    const imageUrl = g.image || DEFAULT_GOAL_IMAGE;
    const improves = Array.isArray(g.improves) ? g.improves : [];

    const imageWrap = document.createElement('div');
    imageWrap.className = 'get-started-goal-card-image-wrap';

    const img = document.createElement('img');
    img.className = 'get-started-goal-card-image';
    img.src = imageUrl;
    img.alt = g.label || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    imageWrap.appendChild(img);
    card.appendChild(imageWrap);

    const body = document.createElement('div');
    body.className = 'get-started-goal-card-body';

    const title = document.createElement('h3');
    title.className = 'get-started-goal-card-title body-semibold-black font-18px';
    title.textContent = g.label || '';
    body.appendChild(title);

    // Commented out for now — keep only the title in the goal card body.
    /*
    const subtitle = document.createElement('p');
    subtitle.className = 'get-started-goal-card-subtitle body-medium-grey font-14px';
    subtitle.textContent = g.description || '';
    body.appendChild(subtitle);

    if (improves.length) {
      const improvesWrap = document.createElement('div');
      improvesWrap.className = 'get-started-goal-card-improves';

      const improvesLabel = document.createElement('span');
      improvesLabel.className =
        'get-started-goal-card-improves-label body-medium-grey font-uppercase-12px';
      improvesLabel.textContent = 'PRIMARY METRICS';
      improvesWrap.appendChild(improvesLabel);

      const tagsWrap = document.createElement('div');
      tagsWrap.className = 'get-started-goal-card-tags';
      improves.forEach((metric) => {
        const tag = document.createElement('span');
        tag.className =
          'get-started-goal-card-tag body-bold-darkgrey font-jetbrains-mono font-jetbrains-mono-12px';
        tag.textContent = metric;
        tagsWrap.appendChild(tag);
      });
      improvesWrap.appendChild(tagsWrap);
      body.appendChild(improvesWrap);
    }
    */

    card.appendChild(body);

    card.addEventListener('click', () => toggleGoal(goalKey));
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleGoal(goalKey);
      }
    });

    return card;
  }

  function renderGoalsGrid() {
    const host = document.getElementById('get-started-goals-grid');
    if (!host || !State.flow) return;
    const devices = Array.isArray(State.answers.devices)
      ? State.answers.devices
      : [];
    const showDeviceLabel = devices.length > 1;

    host.innerHTML = '';
    host.classList.add('get-started-goals-root');

    devices.forEach((deviceId) => {
      const group = document.createElement('div');
      group.className = 'get-started-goals-device-group';

      if (showDeviceLabel) {
        const heading = document.createElement('h3');
        heading.className =
          'get-started-goals-device-heading body-semibold-black font-24px';
        heading.textContent = deviceLabel(deviceId);
        group.appendChild(heading);
      }

      const grid = document.createElement('div');
      grid.className = 'get-started-goal-card-grid';

      goalsForDevice(deviceId).forEach((g) => {
        const goalKey = makeGoalKey(deviceId, g.id);
        grid.appendChild(createGoalCard(goalKey, g));
      });

      group.appendChild(grid);
      host.appendChild(group);
    });

    syncGoalSelectionClasses();
  }

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function toggleGoal(goalKey) {
    const max = State.flow?.nodes?.goals?.maxSelections ?? 3;
    let sel = [...State.answers.goals];
    const idx = sel.indexOf(goalKey);
    if (idx >= 0) {
      sel.splice(idx, 1);
    } else if (sel.length < max) {
      sel.push(goalKey);
    }
    State.answers.goals = sel;
    persist();
    syncGoalSelectionClasses();
    updateNav();
  }

  function syncGoalSelectionClasses() {
    const host = document.getElementById('get-started-goals-grid');
    if (!host) return;
    const sel = new Set(State.answers.goals);
    host.querySelectorAll('.get-started__goal-card').forEach((card) => {
      const key = card.dataset.goalKey;
      const on = Boolean(key && sel.has(key));
      card.classList.toggle('is-selected', on);
      card.classList.toggle('get-started-goal-card--selected', on);
      card.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function syncDeviceUi() {
    const sel = State.answers.devices || [];
    $root.querySelectorAll('[data-get-started-device]').forEach((b) => {
      const did = b.getAttribute('data-get-started-device');
      const on = sel.includes(did);
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function bindDeviceCards() {
    $root.querySelectorAll('[data-get-started-device]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-get-started-device');
        let list = [...(State.answers.devices || [])];
        const pos = list.indexOf(id);
        if (pos >= 0) {
          list.splice(pos, 1);
        } else {
          list.push(id);
        }
        State.answers.devices = list;
        pruneGoalsToDevices();
        persist();
        syncDeviceUi();
        updateNav();
      });
    });
  }

  /** No-op when the count snippet is not rendered (count step skipped in UX). */
  function bindCountButtons() {
    $root.querySelectorAll('[data-get-started-count]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const n = parseInt(btn.getAttribute('data-get-started-count'), 10);
        State.answers.supplementCount = n;
        persist();
        $root.querySelectorAll('[data-get-started-count]').forEach((b) => {
          b.classList.toggle(
            'is-selected',
            parseInt(b.getAttribute('data-get-started-count'), 10) === n
          );
        });
        updateNav();
      });
    });
  }

  function renderResults() {
    syncWindowGetStartedState();
    window.dispatchEvent(new CustomEvent('get-started:results-render'));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateDeviceFloatingContinue();
      });
    });
  }

  /**
   * Coalesce multiple synchronous renderPrecheckout calls in the same turn into one event.
   */
  let precheckoutRenderCoalesceGen = 0;
  function renderPrecheckout() {
    syncWindowGetStartedState();
    precheckoutRenderCoalesceGen += 1;
    const scheduled = precheckoutRenderCoalesceGen;
    queueMicrotask(() => {
      if (scheduled !== precheckoutRenderCoalesceGen) return;
      window.dispatchEvent(new CustomEvent('get-started:precheckout-render'));
    });
  }

  function canContinue() {
    const id = State.stepId;
    if (id === 'device') {
      return (State.answers.devices || []).length >= 1;
    }
    if (id === 'goals') {
      const max = State.flow?.nodes?.goals?.maxSelections ?? 3;
      const n = State.answers.goals.length;
      return n >= 1 && n <= max;
    }
    /* Count step skipped; kept for reference if the panel is re-enabled.
    if (id === 'count') return State.answers.supplementCount != null;
    */
    if (id === 'computing') return false;
    if (id === 'results') return true;
    if (id === 'precheckout') return false;
    return false;
  }

  function isElementInViewport(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  /**
   * Y position (viewport px) of the top edge of the fixed goals footer strip.
   * Measures off-screen when the host is hidden so layout height is known.
   */
  function getGoalsFloatingStripTopPx(floatHostEl) {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (!floatHostEl) return vh;
    let stripH = floatHostEl.getBoundingClientRect().height;
    if (stripH < 2) {
      const saved = floatHostEl.style.cssText;
      const hadHidden = floatHostEl.hasAttribute('hidden');
      if (hadHidden) floatHostEl.removeAttribute('hidden');
      floatHostEl.style.cssText =
        (saved ? `${saved};` : '') +
        'visibility:hidden!important;display:block!important;position:fixed!important;left:-9999px!important;top:0!important;width:100%!important';
      stripH = floatHostEl.getBoundingClientRect().height;
      floatHostEl.style.cssText = saved;
      if (hadHidden) floatHostEl.setAttribute('hidden', '');
    }
    if (stripH < 2) stripH = 96;
    return vh - stripH;
  }

  /** Ignore tiny visible slice when footer is mostly above viewport (overscroll / bounce). */
  const GOALS_FLOAT_FOOTER_SLIVER_PX = 28;

  /**
   * One geometric rule for the goals floating footer (recomputed every update):
   * - Show if the in-flow footer is entirely below the viewport (user has not reached it).
   * - Hide if the footer is entirely above the viewport, or only a small sliver is visible
   *   while the rest is above (fast scroll + rubber-band would otherwise flip "intersects"
   *   and wrongly treat the footer as sitting above the strip).
   * - Otherwise show only when the in-flow footer ends at or above the top of the fixed
   *   strip (no vertical overlap with the strip).
   */
  function shouldShowGoalsFloatingFooter(footerEl, floatHostEl) {
    if (!footerEl) return false;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const rect = footerEl.getBoundingClientRect();

    if (rect.top >= vh) return true;

    if (rect.bottom <= 0) return false;

    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, vh);
    const visibleH = Math.max(0, visibleBottom - visibleTop);
    if (rect.top < 0 && visibleH < GOALS_FLOAT_FOOTER_SLIVER_PX) return false;

    const stripTop = getGoalsFloatingStripTopPx(floatHostEl);
    return rect.bottom <= stripTop;
  }

  function getWindowScrollY() {
    return (
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0
    );
  }

  /**
   * Shared #get-started-floating-wrap: device step uses selection + footer off-screen;
   * results step uses scroll threshold + footer off-screen (same shell as device).
   */
  function updateDeviceFloatingContinue() {
    if (!$floatingWrap || !$next || !$floatingNext) return;
    const isDeviceStep = State.stepId === 'device';
    const isResultsStep = State.stepId === 'results';
    const hasDeviceSelection = (State.answers.devices || []).length >= 1;
    const footerVisible = isElementInViewport($footer);
    const scrolledPastResultsThreshold =
      getWindowScrollY() >= RESULTS_FLOAT_SCROLL_THRESHOLD_PX;

    let show = false;
    if (isDeviceStep) {
      $floatingNext.disabled = !hasDeviceSelection;
      show = hasDeviceSelection && !footerVisible;
    } else if (isResultsStep) {
      $floatingNext.disabled = $next.disabled;
      show = scrolledPastResultsThreshold && !footerVisible;
    } else {
      $floatingNext.disabled = !hasDeviceSelection;
    }

    if (show) {
      if (!floatingVisible) {
        floatingVisible = true;
        if (floatingHideTimer) {
          clearTimeout(floatingHideTimer);
          floatingHideTimer = null;
        }
        $floatingWrap.style.display = 'flex';
        $floatingWrap.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            $floatingWrap.classList.add('get-started-section__floating-wrap--visible');
          });
        });
      } else {
        $floatingWrap.setAttribute('aria-hidden', 'false');
      }
      return;
    }

    if (!floatingVisible) {
      blurActiveElementIfInside($floatingWrap);
      $floatingWrap.setAttribute('aria-hidden', 'true');
      return;
    }
    floatingVisible = false;
    blurActiveElementIfInside($floatingWrap);
    $floatingWrap.setAttribute('aria-hidden', 'true');
    $floatingWrap.classList.remove('get-started-section__floating-wrap--visible');
    if (floatingHideTimer) clearTimeout(floatingHideTimer);
    floatingHideTimer = setTimeout(() => {
      if (!floatingVisible) $floatingWrap.style.display = 'none';
      floatingHideTimer = null;
    }, 300);
  }

  function updateGoalsFloatingFooter() {
    if (!$goalsFloatFooter || !$next || !$footer) return;
    const isGoalsStep = State.stepId === 'goals';
    const count = (State.answers.goals || []).length;
    const hasSelection = count >= 1;
    const footerVisible = isElementInViewport($footer);
    const show = isGoalsStep && hasSelection && !footerVisible;

    if ($goalsFloatNext) {
      $goalsFloatNext.disabled = $next.disabled;
      $goalsFloatNext.hidden = $next.hidden;
    }

    if (show) {
      if (!goalsFloatVisible) {
        goalsFloatVisible = true;
        if (goalsFloatHideTimer) {
          clearTimeout(goalsFloatHideTimer);
          goalsFloatHideTimer = null;
        }
        $goalsFloatFooter.hidden = false;
        $goalsFloatFooter.style.display = 'block';
        $goalsFloatFooter.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            $goalsFloatFooter.classList.add(
              'get-started-section__goals-floating-footer--visible'
            );
          });
        });
      } else {
        $goalsFloatFooter.setAttribute('aria-hidden', 'false');
      }
      return;
    }

    if (!goalsFloatVisible) {
      blurActiveElementIfInside($goalsFloatFooter);
      $goalsFloatFooter.setAttribute('aria-hidden', 'true');
      return;
    }
    goalsFloatVisible = false;
    blurActiveElementIfInside($goalsFloatFooter);
    $goalsFloatFooter.setAttribute('aria-hidden', 'true');
    $goalsFloatFooter.classList.remove(
      'get-started-section__goals-floating-footer--visible'
    );
    if (goalsFloatHideTimer) clearTimeout(goalsFloatHideTimer);
    goalsFloatHideTimer = setTimeout(() => {
      if (!goalsFloatVisible) {
        $goalsFloatFooter.style.display = 'none';
        $goalsFloatFooter.hidden = true;
      }
      goalsFloatHideTimer = null;
    }, 300);
  }

  function updateGoalsFooterContext() {
    if (
      !$footer ||
      !$footerContext ||
      !$footerContextTitle ||
      !$footerContextSubtitle ||
      !$footerContextSubtitleText
    ) {
      return;
    }
    const isGoalsStep = State.stepId === 'goals';
    const selected = Array.isArray(State.answers.goals) ? State.answers.goals : [];
    const count = selected.length;
    const max = State.flow?.nodes?.goals?.maxSelections ?? 3;
    const show = isGoalsStep && count >= 1;
    $footer.classList.toggle('is-with-context', show);
    $footerContext.hidden = !show;

    if ($goalsFloatFooterRow) {
      $goalsFloatFooterRow.classList.toggle('is-with-context', show);
    }
    if ($floatFooterContext) {
      $floatFooterContext.hidden = !show;
    }

    if (!show) {
      if ($floatFooterContextTitle) $floatFooterContextTitle.textContent = '';
      if ($floatFooterContextSubtitleText) $floatFooterContextSubtitleText.textContent = '';
      if ($floatFooterContextCheck) {
        $floatFooterContextCheck.hidden = true;
        $floatFooterContextCheck.setAttribute('aria-hidden', 'true');
      }
      return;
    }

    const labels = selected.map(goalSummaryLabel).filter(Boolean);
    const titleText = labels.join(', ');
    $footerContextTitle.textContent = titleText;
    if ($floatFooterContextTitle) $floatFooterContextTitle.textContent = titleText;

    const remaining = Math.max(0, max - count);
    $footerContextSubtitle.hidden = false;
    if ($floatFooterContextSubtitle) {
      $floatFooterContextSubtitle.hidden = false;
    }

    if (remaining > 0) {
      if ($footerContextCheck) {
        $footerContextCheck.hidden = true;
        $footerContextCheck.setAttribute('aria-hidden', 'true');
      }
      if ($floatFooterContextCheck) {
        $floatFooterContextCheck.hidden = true;
        $floatFooterContextCheck.setAttribute('aria-hidden', 'true');
      }
      const sub = `select ${remaining} more or continue`;
      $footerContextSubtitleText.textContent = sub;
      if ($floatFooterContextSubtitleText) $floatFooterContextSubtitleText.textContent = sub;
    } else {
      if ($footerContextCheck) {
        $footerContextCheck.hidden = false;
        $footerContextCheck.setAttribute('aria-hidden', 'false');
      }
      if ($floatFooterContextCheck) {
        $floatFooterContextCheck.hidden = false;
        $floatFooterContextCheck.setAttribute('aria-hidden', 'false');
      }
      const sub = `${max} goals selected`;
      $footerContextSubtitleText.textContent = sub;
      if ($floatFooterContextSubtitleText) $floatFooterContextSubtitleText.textContent = sub;
    }
  }

  function updateNav() {
    // Back arrow is intentionally disabled in this flow.
    let hideBack = true;
    if ($prev) {
      $prev.hidden = hideBack;
      $prev.disabled = true;
    }
    if ($footer) {
      $footer.classList.toggle('is-no-back', hideBack);
    }
    if ($next) {
      const atEnd = State.stepId === 'precheckout';
      $next.hidden = atEnd;
      if (!atEnd) {
        $next.disabled = !canContinue();
      }
    }
    if ($footer) {
      const hideFooter = State.stepId === 'computing';
      $footer.hidden = hideFooter;
      $footer.setAttribute('aria-hidden', hideFooter ? 'true' : 'false');
      $footer.style.display = hideFooter ? 'none' : '';
    }
    updateGoalsFooterContext();
    updateGoalsFloatingFooter();
    updateDeviceFloatingContinue();
  }

  function pushHistory(nextId) {
    if (State.history[State.history.length - 1] !== nextId) {
      State.history.push(nextId);
    }
  }

  function goTo(stepId, { push = true } = {}) {
    State.stepId = stepId;
    if (push) pushHistory(stepId);
    const skipIndicator =
      stepId === 'results' && activePanelId === 'computing';
    setPanel(stepId, { skipIndicator });
    if (stepId === 'goals') {
      renderGoalsGrid();
    }
    if (stepId === 'results') {
      renderResults();
    }
    if (stepId === 'precheckout') {
      renderPrecheckout();
    }
    persist();
    updateNav();
    trackGetStartedStepPageLoadOnce(stepId);
  }

  function showComputingOnly() {
    State.stepId = 'computing';
    setPanel('computing', { skipIndicator: true });
    persist();
    updateNav();
  }

  async function runComputeThenShowResults() {
    ensureDefaultSupplementCountForSkip();
    showComputingOnly();
    const Compute = window.GetStartedCompute;
    if (!Compute?.compute) {
      console.error('[get-started] GetStartedCompute.compute missing');
      goTo('goals', { push: false });
      return;
    }
    try {
      const computed = await Compute.compute(State.answers);
      State.computed = computed;
      persist();
      goTo('results');
    } catch (e) {
      console.error(e);
      State.stepId = 'goals';
      setPanel('goals');
      persist();
      updateNav();
      trackGetStartedStepPageLoadOnce('goals');
    }
  }

  async function handleNext() {
    const id = State.stepId;
    const flow = State.flow;
    if (!flow?.nodes?.[id]) return;

    if (id === 'device') {
      goTo('goals');
      return;
    }
    if (id === 'goals') {
      await runComputeThenShowResults();
      return;
    }
    if (id === 'results') {
      const nextId = flow.nodes[id]?.next;
      if (nextId && flow.nodes[nextId]) {
        goTo(nextId);
      }
      return;
    }
    /*
    if (id === 'goals') {
      goTo('count');
      return;
    }
    if (id === 'count') {
      showComputingOnly();
      const Compute = window.GetStartedCompute;
      if (!Compute?.compute) {
        console.error('[get-started] GetStartedCompute.compute missing');
        goTo('count', { push: false });
        return;
      }
      try {
        const computed = await Compute.compute(State.answers);
        State.computed = computed;
        persist();
        goTo('results');
      } catch (e) {
        console.error(e);
        State.stepId = 'count';
        setPanel('count');
        persist();
        updateNav();
      }
      return;
    }
    */
  }

  function handlePrev() {
    if (State.stepId === 'computing') {
      State.stepId = 'goals';
      setPanel('goals');
      renderGoalsGrid();
      persist();
      updateNav();
      trackGetStartedStepPageLoadOnce('goals');
      return;
    }
    /*
    if (State.stepId === 'computing') {
      State.stepId = 'count';
      setPanel('count');
      persist();
      updateNav();
      return;
    }
    */
    if (State.history.length <= 1) return;
    State.history.pop();
    const prevId = State.history[State.history.length - 1];
    State.stepId = prevId;
    setPanel(prevId);
    if (prevId === 'goals') renderGoalsGrid();
    persist();
    updateNav();
    trackGetStartedStepPageLoadOnce(prevId);
  }

  function bootPanelsFromZeroThen(stepId) {
    updateStepIndicator(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPanel(stepId);
      });
    });
  }

  function init() {
    window.getStartedState = window.getStartedState || {};
    loadSaved();

    if (!State.stepId) {
      const d = defaultState();
      State.stepId = d.stepId;
      State.answers = d.answers;
      State.history = d.history;
    }
    syncWindowGetStartedState();
    syncSectionInnerPageWidth(State.stepId);

    preloadGetStartedDeviceImages();

    $panels.forEach((el) => {
      el.style.display = 'none';
      el.classList.remove('is-panel-visible');
      el.setAttribute('aria-hidden', 'true');
    });
    activePanelId = null;
    updateStepIndicator(0);

    bindDeviceCards();
    syncDeviceUi();
    bindCountButtons();

    bindStepBarDotClicks();

    if ($prev) $prev.addEventListener('click', handlePrev);
    if ($next) $next.addEventListener('click', handleNext);
    if ($floatingNext) $floatingNext.addEventListener('click', handleNext);
    if ($goalsFloatNext) $goalsFloatNext.addEventListener('click', handleNext);

    let floatingViewportRafPending = false;
    const onFloatingViewportChange = () => {
      if (floatingViewportRafPending) return;
      floatingViewportRafPending = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateGoalsFloatingFooter();
          updateDeviceFloatingContinue();
          floatingViewportRafPending = false;
        });
      });
    };
    window.addEventListener('scroll', onFloatingViewportChange, { passive: true });
    //window.addEventListener('resize', onFloatingViewportChange);

    Storage.onChange(STORAGE_KEY, (payload, meta = {}) => {
      if (!payload) return;
      State.answers = normalizeAnswers(payload.answers || State.answers);
      ensureDefaultSupplementCountForSkip();
      let sid = payload.stepId || State.stepId;
      /* Only migrate legacy “count” saves here — never map “computing” away or the same-tab
         persist callback (~120ms) would jump back to goals and kill the step bar animation. */
      if (sid === 'count') sid = 'goals';
      State.stepId = sid;
      State.computed = payload.computed;
      State.history = normalizeHistorySkipCountStep(
        Array.isArray(payload.history) && payload.history.length
          ? payload.history
          : State.history
      );
      if (State.stepId !== activePanelId) {
        setPanel(State.stepId);
      } else {
        syncSectionInnerPageWidth(State.stepId);
      }
      syncDeviceUi();
      if (State.stepId === 'goals') renderGoalsGrid();
      if (State.stepId === 'results') {
        syncWindowGetStartedState();
        renderResults();
      }
      if (State.stepId === 'precheckout') {
        syncWindowGetStartedState();
        /* Local persist emits ~120ms after goTo; goTo already called renderPrecheckout — skip to avoid a second event. */
        if (meta.remote) {
          renderPrecheckout();
        }
      }
      updateNav();
      if (meta.remote) {
        trackGetStartedStepPageLoadOnce(State.stepId);
      }
    });

    window.getStartedReset = function () {
      Storage.clear(STORAGE_KEY);
      State.stepId = 'device';
      /* Was supplementCount: null — default 4 while count question is skipped. */
      State.answers = { devices: [], goals: [], supplementCount: 4 };
      State.computed = null;
      State.history = ['device'];
      panelTransitionToken += 1;
      $panels.forEach((el) => {
        el.style.display = 'none';
        el.classList.remove('is-panel-visible');
        el.setAttribute('aria-hidden', 'true');
      });
      activePanelId = null;
      updateStepIndicator(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPanel('device');
        });
      });
      syncDeviceUi();
      $root.querySelectorAll('[data-get-started-count]').forEach((b) => {
        b.classList.remove('is-selected');
      });
      const host = document.getElementById('get-started-goals-grid');
      if (host) host.innerHTML = '';
      persist();
      updateNav();
      trackGetStartedStepPageLoadOnce('device');
    };

    fetchFlow(Boot.flowUrl)
      .then((flow) => {
        State.flow = flow;
        preloadGetStartedGoalsAndPillsImages(flow);
        ensureDefaultSupplementCountForSkip();
        bootPanelsFromZeroThen(State.stepId);
        if (State.stepId === 'goals') renderGoalsGrid();
        if (State.stepId === 'results') renderResults();
        if (State.stepId === 'precheckout') renderPrecheckout();
        syncDeviceUi();
        /* Sync count card selection when the count panel is present in Liquid. */
        if (State.answers.supplementCount != null) {
          const n = State.answers.supplementCount;
          $root.querySelectorAll('[data-get-started-count]').forEach((b) => {
            b.classList.toggle(
              'is-selected',
              parseInt(b.getAttribute('data-get-started-count'), 10) === n
            );
          });
        }
        updateNav();
        trackGetStartedStepPageLoadOnce(State.stepId);
        $root.classList.add('is-ready');
        requestAnimationFrame(() => applySectionInnerMinHeightPx());
      })
      .catch((err) => {
        console.error('[get-started]', err);
        const errEl = document.getElementById('get-started-error');
        if (errEl) errEl.hidden = false;
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
